import { API_URL } from "./config";
import moment from "moment";
import { useState, useEffect, useMemo } from "react";
import React from "react";
import { Form, Button, ButtonGroup, Container, Row, Col, Tab, Tabs } from "react-bootstrap";
import './Review.css';
import './Review.scss';
import { FaFilter, FaSearch, FaFilePdf } from "react-icons/fa";
import token from './Token';
import _ from 'lodash';
import { ReviewCalendarViewChart } from './ReviewCalendarViewChart';
import MovingAverageChart from './MovingAverageChart';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

import {
    Calendar,
    Views,
    DateLocalizer,
    momentLocalizer,
} from 'react-big-calendar'

import './CalendarStyle.scss';


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ColoredDateCellWrapper = ({ children }) =>
    React.cloneElement(React.Children.only(children), {
        style: {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
    })



const mLocalizer = momentLocalizer(moment)


function Review() {

    useEffect(() => {
        window.document.title = "Performance Review";
    }, []);

    const localizer = mLocalizer

    const [fromDate, setFromDate] = useState(moment().startOf('day').format('YYYY-MM-DD HH:mm'));
    const [toDate, setToDate] = useState(moment().endOf('day').format('YYYY-MM-DD HH:mm'));
    // For the calendar view: the default date is the date that is currently in the middle of the range
    const [defDate, setDefDate] = useState(moment().startOf('day').set('hour', 2).format('YYYY-MM-DD HH:mm'));
    const [defView, setDefView] = useState('day');

    const [data, setData] = useState([]);
    const [rawData, setRawData] = useState([]);
    const [stats, setStats] = useState({});
    const [groupedByDate, setGroupedByDate] = useState({});

    const [searchTerms, setSearchTerms] = useState('');
    const [movingAverageDays, setMovingAverageDays] = useState(7);
    const [analyticsViewMode, setAnalyticsViewMode] = useState('per-day');

    const replaceTaskIdWithSubtaskName = (data) => {
        const mData = _.cloneDeep(data);
        return mData.map(item => {
            try {
                const subtask = item.note.includes('/') ? item.note.split('/').slice(0, -1).join('/') : item.note;
                return { ...item, taskId: subtask };
            } catch (error) {
                // console.log('item: ', item);
                // note is not present in the item, we assign the taskId to "N/A"
                return { ...item, taskId: "N/A", note: "N/A" };
            }
        });
    }

    const formatHoursDisplay = (hoursValue = 0) => {
        const totalMinutes = Math.round(hoursValue * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    const eventFormatCast = (data, taskId = null, searchTerms = '') => {
        return data.map((item, index) => {
            const endDate = new Date(item.datetime);
            const startDate = new Date(endDate.getTime() - item.duration * 60000); // Convert duration to milliseconds

            if (taskId && item.taskId !== taskId) {
                return null;
            }

            if (searchTerms && !item.note.toLowerCase().includes(searchTerms.toLowerCase())) {
                return null;
            }

            return {
                id: index + 1,
                title: `${item.note}`,
                start: startDate,
                end: endDate,
                duration: item.duration
            };
        }).filter(Boolean);
    };

    const [taskIdFilter, setTaskIdFilter] = useState(null);

    useEffect(() => {

        setGroupedByDate(groupByDate(rawData, taskIdFilter, searchTerms));
        setData(eventFormatCast(rawData, taskIdFilter, searchTerms));
        setStats(groupByTaskId(rawData, taskIdFilter, searchTerms));

    }, [taskIdFilter, searchTerms]);

    const calendarEvents = useMemo(() => data.filter(Boolean), [data]);
    const totalEntries = calendarEvents.length;

    const analyticsStats = useMemo(() => {
        if (!groupedByDate || typeof groupedByDate !== 'object' || Object.keys(groupedByDate).length === 0) {
            return null;
        }

        const entries = Object.entries(groupedByDate)
            .map(([date, minutes]) => ({
                date,
                minutes: Number(minutes) || 0,
            }))
            .filter(entry => entry.minutes >= 0)
            .sort((a, b) => moment(a.date, 'DD/MM/YYYY').valueOf() - moment(b.date, 'DD/MM/YYYY').valueOf());

        if (entries.length === 0) {
            return null;
        }

        const totalMinutes = entries.reduce((sum, entry) => sum + entry.minutes, 0);
        const totalHours = totalMinutes / 60;
        const totalDays = entries.length;
        const workDays = entries.filter(entry => entry.minutes > 0).length;

        const averagePerWorkDay = workDays > 0 ? totalHours / workDays : 0;
        const averagePerDay = totalDays > 0 ? totalHours / totalDays : 0;

        let referenceDate = moment(entries[entries.length - 1].date, 'DD/MM/YYYY', true);
        if (!referenceDate.isValid()) {
            referenceDate = moment(toDate);
        }
        const referenceStart = referenceDate.clone().startOf('day');
        const endOfWeek = referenceDate.clone().endOf('week').startOf('day');
        const endOfMonth = referenceDate.clone().endOf('month').startOf('day');

        const daysRemainingWeek = Math.max(endOfWeek.diff(referenceStart, 'days'), 0);
        const daysRemainingMonth = Math.max(endOfMonth.diff(referenceStart, 'days'), 0);

        const eowProjection = totalHours + daysRemainingWeek * averagePerDay;
        const eomProjection = totalHours + daysRemainingMonth * averagePerDay;

        return {
            totalHoursText: formatHoursDisplay(totalHours),
            averagePerWorkDayText: formatHoursDisplay(averagePerWorkDay),
            averagePerDayText: formatHoursDisplay(averagePerDay),
            endOfWeekProjectionText: formatHoursDisplay(eowProjection),
            endOfMonthProjectionText: formatHoursDisplay(eomProjection),
        };
    }, [groupedByDate, toDate]);

    const groupByDate = (rawData, taskId = null, searchTerms = '') => {
        if (rawData.length === 0) return {};

        // Filter rawData based on taskId
        const filteredData = taskId ? rawData.filter(item => item.taskId === taskId) : rawData;

        // Filter filteredData based on searchTerms
        const filteredDataBySearchTerms = searchTerms ? filteredData.filter(item => item.note.toLowerCase().includes(searchTerms.toLowerCase())) : filteredData;

        if (filteredDataBySearchTerms.length === 0) {
            return {};
        }

        // Find the earliest and latest dates in the rawData
        const dates = filteredDataBySearchTerms.map(item => moment(item.datetime));
        const minDate = moment.min(dates).startOf('day');
        const maxDate = moment(toDate).startOf('day');

        console.log('Max date:', maxDate);

        // Create an object with all dates initialized to 0
        const groupedData = {};
        for (let m = minDate.clone(); m.isSameOrBefore(maxDate); m.add(1, 'days')) {
            groupedData[m.format('DD/MM/YYYY')] = 0;
        }

        // Sum up the durations for each date
        filteredDataBySearchTerms.forEach(item => {
            const date = moment(item.datetime).format('DD/MM/YYYY');
            if (groupedData[date] !== undefined) {
                groupedData[date] += item.duration;
            }
        });

        return groupedData;
    };

    const groupByDateAndTask = (rawData, taskId = null, searchTerms = '') => {
        if (rawData.length === 0) return {};

        // Filter rawData based on taskId
        const filteredData = taskId ? rawData.filter(item => item.taskId === taskId) : rawData;

        // Filter filteredData based on searchTerms
        const filteredDataBySearchTerms = searchTerms ? filteredData.filter(item => item.note.toLowerCase().includes(searchTerms.toLowerCase())) : filteredData;

        // Find the earliest and latest dates in the rawData
        const dates = filteredDataBySearchTerms.map(item => moment(item.datetime));
        if (dates.length === 0) {
            return {};
        }
        const minDate = moment.min(dates).startOf('day');
        const maxDate = moment(toDate).startOf('day');

        // Create an object with all dates initialized with empty task objects
        const groupedData = {};
        for (let m = minDate.clone(); m.isSameOrBefore(maxDate); m.add(1, 'days')) {
            groupedData[m.format('DD/MM/YYYY')] = {};
        }

        // Sum up the durations for each date and task
        filteredDataBySearchTerms.forEach(item => {
            const date = moment(item.datetime).format('DD/MM/YYYY');
            const task = item.taskId || 'N/A';
            if (groupedData[date] !== undefined) {
                if (!groupedData[date][task]) {
                    groupedData[date][task] = 0;
                }
                groupedData[date][task] += item.duration;
            }
        });

        return groupedData;
    };

    const queryTimerHistory = async () => {
        try {
            let datetimeISO = moment(fromDate).toISOString();
            const response = await fetch(`${API_URL}/getTimerHistoryForCalendarView?token=${token}&datetime=${datetimeISO}`);
            let rawData = await response.json();
            rawData = replaceTaskIdWithSubtaskName(rawData);
            setRawData(rawData);
            // console.log(rawData);
            setStats(groupByTaskId(rawData, taskIdFilter, searchTerms));
            // console.log(groupByTaskId(rawData));
            setGroupedByDate(groupByDate(rawData, taskIdFilter, searchTerms));
            setData(eventFormatCast(rawData, taskIdFilter, searchTerms));
            // console.log(groupByDate(rawData, taskIdFilter, searchTerms));
        } catch (error) {
            console.error("Error fetching timer history:", error);
        }
    }



    const setQuickDateRange = (mode) => {
        switch (mode) {
            case 'today':
                setFromDate(moment().startOf('day').format('YYYY-MM-DD HH:mm'));
                setToDate(moment().endOf('day').format('YYYY-MM-DD HH:mm'));
                setDefDate(moment().startOf('day').format('YYYY-MM-DD HH:mm'));
                setDefView('day');
                break;
            case 'yesterday':
                setFromDate(moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm'));
                setToDate(moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD HH:mm'));
                setDefDate(moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm'));
                setDefView('day');
                break;
            case 'this week':
                setFromDate(moment().startOf('week').format('YYYY-MM-DD HH:mm'));
                setToDate(moment().endOf('day').format('YYYY-MM-DD HH:mm'));
                setDefDate(moment().startOf('week').format('YYYY-MM-DD HH:mm'));
                setDefView('week');
                break;
            case 'last week':
                setFromDate(moment().subtract(1, 'weeks').startOf('week').format('YYYY-MM-DD HH:mm'));
                setToDate(moment().subtract(1, 'weeks').endOf('week').format('YYYY-MM-DD HH:mm'));
                setDefDate(moment().subtract(1, 'weeks').startOf('week').format('YYYY-MM-DD HH:mm'));
                setDefView('week');
                break;
            case 'this month':
                setFromDate(moment().startOf('month').format('YYYY-MM-DD HH:mm'));
                setToDate(moment().endOf('day').format('YYYY-MM-DD HH:mm'));
                setDefDate(moment().startOf('month').format('YYYY-MM-DD HH:mm'));
                setDefView('week');
                break;
            case 'last month':
                setFromDate(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD HH:mm'));
                setToDate(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD HH:mm'));
                setDefDate(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD HH:mm'));
                setDefView('week');
                break;
            default:
                console.error('Invalid mode');
        }
    }

    const { components, defaultDate, max, views } = useMemo(
        () => ({
            components: {
                timeSlotWrapper: ColoredDateCellWrapper,
            },
            defaultDate: new Date(),
            max: moment().add(7, 'days').endOf('day').toDate(),
            views: Object.keys(Views).map((k) => Views[k]),
        }),
        []
    )

    const groupByTaskId = (data, taskId = null, searchTerms = '') => {
        // console.log('groupByTaskId', data);
        let totalMinutes = 0;
        let mData = _.cloneDeep(data);
        // Filter mData based on fromDate and toDate
        mData = mData.filter(item => {
            const itemDate = moment(item.datetime);
            return itemDate.isSameOrAfter(moment(fromDate)) && itemDate.isSameOrBefore(moment(toDate));
        });

        if (taskId) {
            mData = mData.filter(item => item.taskId === taskId);
        }

        if (searchTerms) {
            mData = mData.filter(item => item.note.toLowerCase().includes(searchTerms.toLowerCase()));
        }

        const groupedData = mData.reduce((acc, item) => {
            if (!acc[item.taskId]) {
                acc[item.taskId] = 0;
            }
            acc[item.taskId] += item.duration;
            totalMinutes += item.duration;
            return acc;
        }, {});

        // Convert minutes to a readable format (HH:MM)
        const formatDuration = (minutes) => {
            // convert minutes to integer
            minutes = parseInt(minutes);
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
        };


        return {
            totalMinutes: formatDuration(totalMinutes),
            data: Object.entries(groupedData).map(([taskId, duration]) => ({
                taskId,
                duration: Math.round(duration), // Rounding to nearest minute
                durationStr: formatDuration(Number(duration))
            }))
        };
    };

    const handleNavigate = (date) => {
        setDefDate(date);
    };

    const handleBarClick = (dateString) => {
        // dateString is in format DD/MM/YYYY
        const parsedDate = moment(dateString, 'DD/MM/YYYY');
        if (parsedDate.isValid()) {
            setDefDate(parsedDate.format('YYYY-MM-DD HH:mm'));
            setDefView('day');
        }
    };


    const handleFromDateChange = (e) => {
        let xdString = e.target.value
        if (xdString.indexOf('d') > -1) {
            try {
                // remove the 'd' character
                xdString = xdString.replace('d', '')
                // convert to number
                let xd = Number(xdString)
                // convert to Date
                let fromDate = moment().add(xd, 'days').startOf('day').format("YYYY-MM-DD HH:mm");
                setFromDate(fromDate);
            } catch (error) {
                setFromDate(xdString);
            }
        } else {
            setFromDate(e.target.value);
        }
    }

    const handleToDateChange = (e) => {
        let xdString = e.target.value
        if (xdString.indexOf('d') > -1) {
            try {
                xdString = xdString.replace('d', '')
                let xd = Number(xdString)
                let toDate = moment().add(xd, 'days').endOf('day').format("YYYY-MM-DD HH:mm");
                setToDate(toDate);
            } catch (error) {
                setToDate(xdString);
            }
        } else {
            setToDate(e.target.value);
        }
    }


    return (
        <div className="AppRootDesktop">
            <div className='AppRootInnerOverlay'>
                <Container fluid className="edit-page review-page">
                    <div className="action-bar">
                        <h1>Performance Review</h1>
                        <div className="d-flex align-items-center flex-wrap justify-content-end gap-3">
                            <div className="action-bar-stats">
                                <div>Total tracked: {stats?.totalMinutes ?? '00:00'}</div>
                                <div>Entries: {totalEntries}</div>
                            </div>
                            <div className="">
                                <Button variant="success" onClick={queryTimerHistory}>
                                    <FaSearch /> Search
                                </Button>
                                <Button
                                    variant="warning"
                                    onClick={() => {
                                        const startdate = moment(fromDate).toISOString();
                                        const enddate = moment(toDate).toISOString();
                                        window.open(`${API_URL}/transactionRecap?token=${token}&startdate=${startdate}&enddate=${enddate}`, '_blank');
                                    }}
                                >
                                    <FaFilePdf /> PDF
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="BodyEdit">
                        {/* Time Range & Quick Ranges - Merged */}
                        <Row className="g-4">
                            <Col xs={12}>
                                <div>
                                    <h5 className="mb-3 text-uppercase">Time Range</h5>

                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>From</Form.Label>
                                                <Form.Control type="datetime" value={fromDate} onChange={(e) => handleFromDateChange(e)} />
                                                {moment(fromDate, "YYYY-MM-DDTHH:mm").isValid() && (
                                                    <div className="friendly-date">
                                                        {moment(fromDate).format("dddd, MMMM D, YYYY")}
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>To</Form.Label>
                                                <Form.Control type="datetime" value={toDate} onChange={(e) => handleToDateChange(e)} />
                                                {moment(toDate, "YYYY-MM-DDTHH:mm").isValid() && (
                                                    <div className="friendly-date">
                                                        {moment(toDate).format("dddd, MMMM D, YYYY")}
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Search terms</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter search terms"
                                                    value={searchTerms}
                                                    onChange={(e) => setSearchTerms(e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="quick-ranges-section mt-4">
                                        <h6 className="mb-3 text-uppercase text-muted">Quick Ranges</h6>
                                        <div className="QuickDateRangePicker">
                                            <div className="button-grid w-100">
                                                <div className="button-cell">
                                                    <Button variant="outline-primary" className="w-100" onClick={() => setQuickDateRange('today')}>Today</Button>
                                                </div>
                                                <div className="button-cell">
                                                    <Button variant="outline-primary" className="w-100" onClick={() => setQuickDateRange('yesterday')}>Yesterday</Button>
                                                </div>
                                                <div className="button-cell">
                                                    <Button variant="outline-primary" className="w-100" onClick={() => setQuickDateRange('this week')}>This Week</Button>
                                                </div>
                                                <div className="button-cell">
                                                    <Button variant="outline-primary" className="w-100" onClick={() => setQuickDateRange('last week')}>Last Week</Button>
                                                </div>
                                                <div className="button-cell">
                                                    <Button variant="outline-primary" className="w-100" onClick={() => setQuickDateRange('this month')}>This Month</Button>
                                                </div>
                                                <div className="button-cell">
                                                    <Button variant="outline-primary" className="w-100" onClick={() => setQuickDateRange('last month')}>Last Month</Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>


                        <div className="mt-4">
                            <Tabs defaultActiveKey="statistics" id="review-tabs" className="mb-3">
                                <Tab eventKey="statistics" title="Statistics">
                                    {/* Task Breakdown - Full Width */}
                                    <Row className="g-4 mt-1">
                                        <Col xs={12}>
                                            <div className="task-card">
                                                <h5 className="mb-3 text-uppercase">Task Breakdown</h5>
                                                <div className="task-breakdown-content">
                                                    {stats?.data && stats.data.length > 0 ? (
                                                        <div className="task-breakdown-list">
                                                            {stats.data.map((item) => (
                                                                <div
                                                                    key={item.taskId}
                                                                    className={`task-breakdown-item ${item.taskId === taskIdFilter ? 'active' : ''}`}
                                                                    onClick={() => item.taskId === taskIdFilter ? setTaskIdFilter(null) : setTaskIdFilter(item.taskId)}
                                                                >
                                                                    <span className="task-name">{item.taskId}</span>
                                                                    <span className="task-duration">
                                                                        {item.durationStr} {item.taskId === taskIdFilter ? <FaFilter /> : ''}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="no-tasks-message">No tasks found for the selected filters.</div>
                                                    )}
                                                    <div className="TotalMinutes">Total: {stats?.totalMinutes ?? '00:00'}</div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Daily Totals & Moving Average - Merged */}
                                    <Row className="g-4 mt-1">
                                        <Col xs={12}>
                                            <div className="task-card">
                                                <h5 className="mb-4 text-uppercase">Analytics</h5>

                                                <div className="analytics-section mb-5">
                                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                                                        <h6 className="mb-0 text-uppercase text-muted">
                                                            {analyticsViewMode === 'ma' ? 'Moving Average' : 'Daily Totals'}
                                                        </h6>
                                                        <div className="d-flex align-items-center gap-3">
                                                            {analyticsViewMode === 'ma' && (
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span>Days:</span>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="2"
                                                                        max="30"
                                                                        value={movingAverageDays}
                                                                        onChange={(e) => setMovingAverageDays(parseInt(e.target.value) || 7)}
                                                                        style={{ width: '80px' }}
                                                                    />
                                                                </div>
                                                            )}
                                                            <ButtonGroup size="sm">
                                                                <Button
                                                                    variant={analyticsViewMode === 'per-day' ? 'primary' : 'outline-primary'}
                                                                    onClick={() => setAnalyticsViewMode('per-day')}
                                                                >
                                                                    Per-day
                                                                </Button>
                                                                <Button
                                                                    variant={analyticsViewMode === 'cumulative' ? 'primary' : 'outline-primary'}
                                                                    onClick={() => setAnalyticsViewMode('cumulative')}
                                                                >
                                                                    Cumulative
                                                                </Button>
                                                                <Button
                                                                    variant={analyticsViewMode === 'ma' ? 'primary' : 'outline-primary'}
                                                                    onClick={() => setAnalyticsViewMode('ma')}
                                                                >
                                                                    MA
                                                                </Button>
                                                            </ButtonGroup>
                                                        </div>
                                                    </div>
                                                    {analyticsViewMode === 'ma' ? (
                                                        <MovingAverageChart
                                                            groupedData={groupedByDate}
                                                            days={movingAverageDays}
                                                            title={`${movingAverageDays}-Day Moving Average`}
                                                        />
                                                    ) : (
                                                        <ReviewCalendarViewChart
                                                            groupedData={groupByDateAndTask(rawData, taskIdFilter, searchTerms)}
                                                            onBarClick={handleBarClick}
                                                            viewMode={analyticsViewMode}
                                                        />
                                                    )}
                                                    {analyticsStats ? (
                                                        <div className="analytics-stats mt-4">
                                                            <Row className="g-3">
                                                                <Col xs={12} md={6}>
                                                                    <div className="stat-tile p-3 rounded bg-dark bg-opacity-50 h-100">
                                                                        <div className="text-uppercase text-muted small mb-1">Total hours accumulated</div>
                                                                        <div className="h5 mb-0">{analyticsStats.totalHoursText}</div>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={12} md={6}>
                                                                    <div className="stat-tile p-3 rounded bg-dark bg-opacity-50 h-100">
                                                                        <div className="text-uppercase text-muted small mb-1">Average hour per work-day</div>
                                                                        <div className="h5 mb-0">{analyticsStats.averagePerWorkDayText}</div>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={12} md={6}>
                                                                    <div className="stat-tile p-3 rounded bg-dark bg-opacity-50 h-100">
                                                                        <div className="text-uppercase text-muted small mb-1">Average hour per day</div>
                                                                        <div className="h5 mb-0">{analyticsStats.averagePerDayText}</div>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={12} md={6}>
                                                                    <div className="stat-tile p-3 rounded bg-dark bg-opacity-50 h-100">
                                                                        <div className="text-uppercase text-muted small mb-1">End-of-week projection</div>
                                                                        <div className="h5 mb-0">{analyticsStats.endOfWeekProjectionText}</div>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={12} md={6}>
                                                                    <div className="stat-tile p-3 rounded bg-dark bg-opacity-50 h-100">
                                                                        <div className="text-uppercase text-muted small mb-1">End-of-month projection</div>
                                                                        <div className="h5 mb-0">{analyticsStats.endOfMonthProjectionText}</div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center text-muted mt-3">No statistics available for the selected filters.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Tab>
                                <Tab eventKey="calendar" title="Calendar">
                                    <Row className="g-4 mt-1">
                                        <Col xs={12}>
                                            <div className="CalendarDivvv task-card rounded-10">
                                                <Calendar
                                                    components={components}
                                                    date={moment(defDate).toDate()}
                                                    events={calendarEvents}
                                                    localizer={localizer}
                                                    max={max}
                                                    showMultiDayTimes
                                                    step={15}
                                                    views={views}
                                                    popup={true}
                                                    onNavigate={handleNavigate}
                                                    onView={(view) => {
                                                        setDefView(view);
                                                    }}
                                                    view={defView}
                                                    onSelectEvent={(event) => {
                                                        alert(`Event: ${event.title}`);
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </Tab>
                            </Tabs>
                        </div>
                    </div>
                </Container>
            </div>
        </div>
    )
}

export default Review;
